import { endpoints } from "@/lib/api/endpoints";
import { buildRequestUrl } from "@/lib/api/client";

type DownloadOutcome = { ok: boolean; notReady: boolean };

/**
 * Opens the invoice (a PDF the API renders at request time).
 *
 * Opening a *new tab* is unreliable on real mobile: iOS Safari treats
 * `window.open` with a features string as a popup and blocks it, and in-app
 * webviews (WhatsApp/Instagram/Facebook — where sellers often open links) block
 * scripted popups and frequently ignore `target="_blank"` entirely. So on
 * touch devices we navigate the *current* tab straight to the PDF: this works
 * everywhere, the browser's built-in PDF viewer handles it, and Back returns to
 * the app. On desktop (fine pointer) we keep a new tab via a real anchor click
 * so the orders view stays open.
 *
 * Cookie auth rides the same-origin top-level navigation. A not-yet-settled
 * order 404s, but the button is hidden until the order settles, so that's a
 * rare race only. Returns `ok: true` — a navigation/link activation has no
 * reliable success signal (and `window.open` with `noopener` always returned
 * null, so the old check was a false negative that fired a spurious error
 * toast even when the tab opened).
 */
export function useOrderInvoiceDownload() {
  const download = async (orderId: string): Promise<DownloadOutcome> => {
    const url = buildRequestUrl(endpoints.seller.orderInvoice(orderId));

    const isTouch =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;

    if (isTouch) {
      // Bulletproof on mobile + in-app webviews.
      window.location.assign(url);
      return { ok: true, notReady: false };
    }

    // Desktop: genuine anchor click opens a new tab and keeps the app open.
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.target = "_blank";
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    return { ok: true, notReady: false };
  };

  return { download, isLoading: false };
}
